import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Trash2, Pencil, RefreshCw, Check } from "lucide-react";
import { FileUpload } from "@/components/admin/FileUpload";
import { Checkbox } from "@/components/ui/checkbox";

interface AdminUser {
    id: number;
    username: string;
    name: string;
    is_superuser: number;
    profile_image: string;
    permissions: string | string[]; // JSON string or array
    created_at: string;
}

const MODULES = [
    { id: "dashboard", label: "Dashboard" },
    { id: "circuits", label: "Circuits" },
    { id: "stories", label: "Stories" },
    { id: "gallery", label: "Gallery" },
    { id: "metadata", label: "Metadata" },
    { id: "legal", label: "Legal Pages" },
    { id: "inquiries", label: "Inquiries" },
    { id: "admins", label: "Admin Users" },
];

export default function AdminManager() {
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        username: "",
        password: "",
        name: "",
        is_superuser: false,
        profile_image: "",
        permissions: [] as string[]
    });
    const { toast } = useToast();

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            const data = await fetchAPI("?type=admin_users");
            // Normalize permissions
            const normalized = data.map((u: any) => ({
                ...u,
                permissions: typeof u.permissions === 'string' ? JSON.parse(u.permissions || '[]') : u.permissions || []
            }));
            setAdmins(normalized);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (admin?: AdminUser) => {
        if (admin) {
            setEditingId(admin.id);
            setFormData({
                username: admin.username,
                password: "", // Leave empty to keep
                name: admin.name || "",
                is_superuser: !!admin.is_superuser,
                profile_image: admin.profile_image || "",
                permissions: Array.isArray(admin.permissions) ? admin.permissions : []
            });
        } else {
            setEditingId(null);
            setFormData({
                username: "",
                password: "",
                name: "",
                is_superuser: false,
                profile_image: "",
                permissions: []
            });
        }
        setIsDialogOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const method = editingId ? "PUT" : "POST";
            const url = editingId ? `?type=admin_users&id=${editingId}` : "?type=admin_users";
            
            const result = await fetchAPI(url, {
                method: method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });

            if (result.status === "success") {
                toast({ title: "Success", description: editingId ? "Admin updated" : "Admin added" });
                setIsDialogOpen(false);
                fetchAdmins();
            } else {
                throw new Error(result.message);
            }
        } catch (error: any) {
            toast({ variant: "destructive", title: "Error", description: error.message });
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm("Delete this admin user?")) return;
        try {
            const result = await fetchAPI(`?type=admin_users&id=${id}`, { method: "DELETE" });
            if (result.status === "success") {
                toast({ title: "Deleted", description: "User removed" });
                fetchAdmins();
            } else {
                throw new Error(result.message);
            }
        } catch (error: any) {
            toast({ variant: "destructive", title: "Cannot Delete", description: error.message });
        }
    };

    const togglePermission = (moduleId: string) => {
        setFormData(prev => {
            if (prev.permissions.includes(moduleId)) {
                return { ...prev, permissions: prev.permissions.filter(p => p !== moduleId) };
            } else {
                return { ...prev, permissions: [...prev.permissions, moduleId] };
            }
        });
    };

    if (!localStorage.getItem("is_superuser")) {
        return <div className="p-8">Access Denied. Superuser privileges required.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-serif font-bold">Admin Users</h1>
                <Button onClick={() => handleOpenDialog()} className="gap-2">
                    <Plus className="w-4 h-4" /> Add Admin
                </Button>
            </div>

            <div className="border rounded-lg bg-card text-card-foreground shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Avatar</TableHead>
                            <TableHead>User Details</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Permissions</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} className="text-center py-4">Loading...</TableCell></TableRow>
                        ) : admins.map(admin => (
                            <TableRow key={admin.id}>
                                <TableCell>
                                    {admin.profile_image ? (
                                        <img src={admin.profile_image} className="w-8 h-8 rounded-full object-cover" loading="lazy" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold">
                                            {admin.username[0].toUpperCase()}
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="font-medium">{admin.name || admin.username}</div>
                                    <div className="text-xs text-muted-foreground">@{admin.username}</div>
                                </TableCell>
                                <TableCell>
                                    {admin.is_superuser == 1 ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                            Superuser
                                        </span>
                                    ) : (
                                        <span className="text-sm text-muted-foreground">Admin</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {admin.is_superuser == 1 ? (
                                        <span className="text-xs text-muted-foreground">All Access</span>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {(Array.isArray(admin.permissions) ? admin.permissions : []).map((p: string) => (
                                                <span key={p} className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-secondary text-secondary-foreground border">
                                                    {MODULES.find(m => m.id === p)?.label || p}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(admin)}>
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(admin.id)} disabled={admin.username === 'admin'}>
                                            <Trash2 className="w-4 h-4 text-destructive" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Edit Admin" : "Add New Admin"}</DialogTitle>
                        <DialogDescription>
                            {editingId ? "Modify user details. Leave password blank to keep current password." : "Create a new administrator account."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Username</Label>
                                <Input 
                                    value={formData.username} 
                                    onChange={e => setFormData({...formData, username: e.target.value})} 
                                    required 
                                    disabled={!!editingId} // Prevent changing username for now
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Full Name</Label>
                                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. John Doe" />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Password {editingId && "(Optional)"}</Label>
                            <div className="flex gap-2">
                                <Input 
                                    type="password" 
                                    value={formData.password} 
                                    onChange={e => setFormData({...formData, password: e.target.value})} 
                                    required={!editingId}
                                    placeholder={editingId ? "Enter new password to reset" : "Strong password"} 
                                />
                                {editingId && formData.password && (
                                    <Button type="button" variant="outline" size="icon" onClick={() => setFormData({...formData, password: ""})} title="Undo">
                                        <RefreshCw className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Profile Image</Label>
                            <FileUpload value={formData.profile_image} onChange={url => setFormData({...formData, profile_image: url})} accept="image/*" />
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/20">
                            <div className="space-y-0.5">
                                <Label>Superuser Status</Label>
                                <p className="text-xs text-muted-foreground">Superusers have full access to everything.</p>
                            </div>
                            <Switch checked={formData.is_superuser} onCheckedChange={checked => setFormData({...formData, is_superuser: checked})} />
                        </div>

                        {!formData.is_superuser && (
                            <div className="space-y-3">
                                <Label>Permissions</Label>
                                <div className="grid grid-cols-2 gap-4 border p-4 rounded-lg">
                                    {MODULES.map(module => (
                                        <div key={module.id} className="flex items-center space-x-2">
                                            <Checkbox 
                                                id={`perm-${module.id}`} 
                                                checked={formData.permissions.includes(module.id)}
                                                onCheckedChange={() => togglePermission(module.id)}
                                            />
                                            <label
                                                htmlFor={`perm-${module.id}`}
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                            >
                                                {module.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                            <Button type="submit">{editingId ? "Save Changes" : "Create User"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
